import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { BasePage, PageType } from 'src/components/BasePage';
import {
    FormioCustomEventComponent,
    handleStartProcessCustomEvent,
} from 'src/components/EntityPresentation/utils/handleStartProcessCustomEvent';
import { FormioForm } from 'src/components/FormioForm/FormioForm';
import { SpinnerFullWidth } from 'src/components/Spinner/SpinnerFullWidth';
import { Router } from 'src/core/router/Router';
import { BasicStore } from 'src/utils/mobx/BasicStore/BasicStore';
import { Entity } from 'src/utils/mobx/BasicStore/BasicStore.types';
import { CombinedLoadingStatus } from 'src/utils/mobx/CombinedLoadingStatus';

type DetailsPageProps = {
    pageName: string;
    store: BasicStore<Entity>;
};

export const DetailsPage = observer(({ pageName, store }: DetailsPageProps) => {
    const {
        params: { id },
        url,
    } = useRouteMatch<{ id: string }>();

    useEffect(() => {
        store.loadForm(store.itemDetailsFormKey);
        store.loadItem(id);

        return () => {
            store.formLoader.reset();
            store.itemLoader.reset();
        };
    }, []);

    const loadingStatus = useMemo(() => {
        return new CombinedLoadingStatus([store.formLoader, store.itemLoader]);
    }, []);

    if (loadingStatus.isLoading) {
        return <SpinnerFullWidth />;
    }

    return (
        <BasePage
            pageName={`${pageName}-${id}`}
            type={PageType.FULL}
            data-testid={`${pageName}-${id}`}
        >
            <FormioForm
                form={store.currentItemForm!}
                submission={{ data: store.currentItem }}
                onCustomEvent={(event) => {
                    if (event.type === 'startProcess') {
                        handleStartProcessCustomEvent(event, url);
                    }

                    if (event.type === 'edit') {
                        Router.navigate(`${url}/edit`);
                    }

                    if (event.type === 'create') {
                        const entity = (
                            event.component as FormioCustomEventComponent
                        ).properties?.entity;
                        const queryParams = (
                            event.component as FormioCustomEventComponent
                        ).properties?.urlParams;
                        Router.navigate(`/${entity}/create?${queryParams}`);
                    }

                    return Promise.resolve();
                }}
            />
        </BasePage>
    );
});
